import { useState, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import timeEntryService from '@api/v1/timeEntryService'
import { api } from '@api/v1/client'
import DataGrid from '@components/common/DataGrid'
import BillingChat from '@components/billing/BillingChat'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'

const TABS = [
  { id: 'time', label: 'Time Entries' },
  { id: 'bills', label: 'Bills' },
  { id: 'payments', label: 'Payments' },
]

/**
 * CC-53132: the surface a read that never produced an answer is allowed to render.
 *
 * It exists because 'blocked' and 'failed' are not "you have nothing" and must never
 * borrow that copy. A paused fetch is the worse of the two: `queryFn` is never called,
 * so there is no request in any log on either side, and the only thing that ever tells
 * anyone it happened is a message like this one. `role="alert"` because the user is
 * looking at a page that already appears to have answered them.
 *
 * The retry is the point of the whole surface. Without it a blocked read leaves the
 * user with nothing to do but reload the app and hope, which is exactly what they did
 * instead of reporting it.
 */
const UnreadableNotice = ({ state, subject, message, onRetry }) => {
  const blocked = state === 'blocked'
  return (
    <div
      role="alert"
      className={`mb-4 rounded-xl border p-4 text-sm ${
        blocked ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-red-100 bg-red-50 text-red-700'
      }`}
    >
      <p className="font-medium">
        {blocked
          ? `Can’t reach the server, so we haven’t loaded your ${subject}.`
          : `We couldn’t load your ${subject}: ${message}`}
      </p>
      <p className={`mt-1 ${blocked ? 'text-amber-800' : ''}`}>
        This is not an empty list and it is not a zero — nothing was read, so no figure
        on this screen is describing your data.
        {blocked ? ' Your connection looks offline; check it and try again.' : ''}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className={`mt-3 rounded-lg border bg-white px-3 py-1.5 text-xs font-medium ${
          blocked
            ? 'border-amber-300 text-amber-900 hover:bg-amber-100'
            : 'border-red-200 text-red-700 hover:bg-red-100'
        }`}
      >
        Try again
      </button>
    </div>
  )
}

const Billing = () => {
  useDocumentTitle('Billing')
  const [activeTab, setActiveTab] = useState('time')
  const [showChat, setShowChat] = useState(false)
  const queryClient = useQueryClient()

  // Time entries
  const timeQuery = useQuery({
    queryKey: ['billingTimeEntries'],
    queryFn: () => timeEntryService.getBillingEntries({ limit: 500 }),
    enabled: activeTab === 'time',
  })

  // Bills via generic objects endpoint (type 30)
  const billsQuery = useQuery({
    queryKey: ['billingBills'],
    queryFn: () => api.get('/objects/30', { limit: 500 }),
    enabled: activeTab === 'bills',
  })

  // Payments via generic objects endpoint (type varies by tenant, try common ones)
  const paymentsQuery = useQuery({
    queryKey: ['billingPayments'],
    queryFn: () => api.get('/objects/1009', { limit: 500 }),
    enabled: activeTab === 'payments',
  })

  /*
   * CC-53132 — every number on this page came from `isLoading`, and `isLoading` is
   * `isPending && isFetching`, so it is FALSE for a query that is pending but not in
   * flight. The state that matters is `fetchStatus: 'paused'`: with react-query's
   * default `networkMode: 'online'`, a query does not fire while `onlineManager`
   * believes the browser is offline. `queryFn` is never called, nothing throws,
   * nothing logs, and `data` stays `undefined`.
   *
   * On this page that produced the worst possible rendering of the worst possible
   * read: three summary cards reading **Total Hours 0.0 / Billable Hours 0.0 /
   * Total Value $0.00**, under a grid saying "No time entries found", with no
   * spinner and no error, indefinitely. In a firm's billing screen that is not a
   * blank state — it is a statement that nobody billed anything, and it is one a
   * timekeeper could act on.
   *
   * `useReadState` rather than the bare `readState` so each of the three reads
   * carries the permanent blocked-read diagnostic; a paused fetch leaves no other
   * trace by construction.
   */
  const timeState = useReadState('billing-time-entries', timeQuery)
  const billsState = useReadState('billing-bills', billsQuery)
  const paymentsState = useReadState('billing-payments', paymentsQuery)

  const { data: timeData, isLoading: timeLoading } = timeQuery
  const { data: billsResp, isLoading: billsLoading } = billsQuery
  const { data: paymentsResp, isLoading: paymentsLoading } = paymentsQuery

  const timeColumns = useMemo(() => [
    { id: 'date', header: 'Date', accessorKey: 'date',
      cell: ({ getValue }) => { const v = getValue(); return v ? dayjs(v).format('M/D/YYYY') : '' },
      size: 100 },
    { id: 'matterName', header: 'Matter', accessorKey: 'matterName', size: 200 },
    { id: 'description', header: 'Description', accessorKey: 'description', size: 250 },
    { id: 'timekeeper', header: 'Timekeeper', accessorKey: 'timekeeper', size: 150 },
    { id: 'billingCode', header: 'Code', accessorKey: 'billingCode', size: 100 },
    { id: 'hours', header: 'Hours', accessorKey: 'hours',
      cell: ({ getValue }) => <span className="font-medium">{Number(getValue() || 0).toFixed(1)}</span>,
      size: 80 },
    { id: 'billableValue', header: 'Amount', accessorKey: 'billableValue',
      cell: ({ getValue }) => {
        const v = Number(getValue() || 0)
        return <span className="font-medium">${v.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
      },
      size: 100 },
    { id: 'isNonBillable', header: 'Billable', accessorFn: (row) => row.isNonBillable ? 'No' : 'Yes',
      cell: ({ getValue }) => getValue() === 'Yes'
        ? <span className="text-xs font-medium text-cb-teal-dark bg-cb-teal/10 px-2 py-0.5 rounded-full">Yes</span>
        : <span className="text-xs text-gray-500">No</span>,
      size: 80 },
  ], [])

  const billColumns = useMemo(() => {
    const fields = billsResp?.meta?.fields || []
    const cols = [{ id: 'name', header: 'Name', accessorKey: 'name', size: 200 }]
    for (const field of fields.slice(0, 10)) {
      cols.push({ id: field.id, header: field.label, accessorKey: field.id, size: 150 })
    }
    return cols
  }, [billsResp])

  const paymentColumns = useMemo(() => {
    const fields = paymentsResp?.meta?.fields || []
    const cols = [{ id: 'name', header: 'Name', accessorKey: 'name', size: 200 }]
    for (const field of fields.slice(0, 10)) {
      cols.push({ id: field.id, header: field.label, accessorKey: field.id, size: 150 })
    }
    return cols
  }, [paymentsResp])

  /*
   * The `|| []` is gone from all three. It was the bug in one character: it turned
   * "we never got an answer" into "the answer was none" before the grid could tell
   * them apart. `DataGrid` now distinguishes `undefined` from `[]` itself and prints
   * an honest `role="alert"` instead of `emptyMessage`, plus no row count — so the
   * page's whole job here is to STOP collapsing, and let `undefined` through.
   *
   * A resolved response that carries no array lands here as `undefined` too, and that
   * is deliberate: a body we cannot read is not a body that said "nothing" (the
   * `readShape.js` rule, same family).
   */
  const timeEntries = timeData?.entries
  const bills = billsResp?.data
  const payments = paymentsResp?.data

  /*
   * The three summary cards, which are the reason this page's version of the defect
   * is worse than the catalog's. They are sums over the time-entry read, so on any
   * unresolved read they are not small numbers — they are numbers nobody computed.
   * `null` here means "no figure to show", and the cards render an em dash.
   *
   * They stay bound to the TIME read alone (as they always were), including while a
   * different tab is showing: switching to Bills does not change what the firm billed.
   */
  const totals = useMemo(() => {
    if (!Array.isArray(timeEntries)) return null
    return {
      hours: timeEntries.reduce((s, e) => s + Number(e.hours || 0), 0),
      billable: timeEntries.filter((e) => !e.isNonBillable).reduce((s, e) => s + Number(e.hours || 0), 0),
      value: timeEntries.reduce((s, e) => s + Number(e.billableValue || 0), 0),
    }
  }, [timeEntries])

  /*
   * Only 'blocked' and 'failed' get the notice.
   *
   * 'idle' deliberately does NOT, and this page is the one where that distinction is
   * real rather than theoretical: all three queries are conditionally `enabled` on the
   * active tab, so the time query genuinely sits idle whenever the user is on Bills or
   * Payments. Idle is "not asked yet" — an ordinary, correct state, and dressing it as
   * a failure would put a red panel on the page for a read nobody wanted. It also must
   * not be folded into loading: there is no spinner that would ever stop.
   *
   * The em dash covers idle, and covers it honestly: no figure, because none was read.
   */
  const timeUnreadable = timeState === 'blocked' || timeState === 'failed'
  const billsUnreadable = billsState === 'blocked' || billsState === 'failed'
  const paymentsUnreadable = paymentsState === 'blocked' || paymentsState === 'failed'

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display text-gray-900">Billing</h1>
          <p className="text-sm text-gray-500 mt-1">Manage time entries, bills, and payments</p>
        </div>
        <button
          onClick={() => setShowChat(!showChat)}
          className="flex items-center gap-2 px-4 py-2.5 bg-cb-accent text-white text-sm font-medium rounded-lg hover:bg-cb-accent/90 transition-colors shadow-sm"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
          </svg>
          Quick Entry
        </button>
      </div>

      {/* The notice sits above the cards, not beside the grid, because the cards are
          what it explains: they are on screen on every tab and three em dashes with
          no reason given is its own small mystery. */}
      {timeUnreadable && (
        <UnreadableNotice
          state={timeState}
          subject="time entries"
          message={timeQuery.error?.message}
          onRetry={() => timeQuery.refetch()}
        />
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <p className="text-sm font-medium text-gray-500 mb-1">Total Hours</p>
          <p className="text-2xl font-semibold text-gray-900 font-display">
            {totals ? totals.hours.toFixed(1) : '—'}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <p className="text-sm font-medium text-gray-500 mb-1">Billable Hours</p>
          <p className="text-2xl font-semibold text-gray-900 font-display">
            {totals ? totals.billable.toFixed(1) : '—'}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <p className="text-sm font-medium text-gray-500 mb-1">Total Value</p>
          <p className="text-2xl font-semibold text-gray-900 font-display">
            {totals ? `$${totals.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content. Each grid's `data` may be `undefined`, which is the whole fix:
          the grid renders `unresolvedMessage` under `role="alert"` and no row count.
          None of them can see 'idle' — a tab's query is `enabled` on exactly the
          condition that mounts its grid — so there is no "not asked yet" branch to
          write here; the em-dashed cards above are where idle actually shows up. */}
      {activeTab === 'time' && (
        <DataGrid
          columns={timeColumns}
          data={timeEntries}
          isLoading={timeLoading}
          emptyMessage="No time entries found"
          unresolvedMessage="Your time entries could not be loaded, so this is not a period with no time in it. Try again."
          loadingMessage="Loading time entries..."
          exportFilename="billing-time-entries"
          pageSize={50}
        />
      )}
      {activeTab === 'bills' && (
        <>
          {billsUnreadable && (
            <UnreadableNotice
              state={billsState}
              subject="bills"
              message={billsQuery.error?.message}
              onRetry={() => billsQuery.refetch()}
            />
          )}
          <DataGrid
            columns={billColumns}
            data={bills}
            isLoading={billsLoading}
            emptyMessage="No bills found"
            unresolvedMessage="Your bills could not be loaded, so this is not a firm with no bills. Try again."
            loadingMessage="Loading bills..."
            exportFilename="bills"
            pageSize={50}
          />
        </>
      )}
      {activeTab === 'payments' && (
        <>
          {paymentsUnreadable && (
            <UnreadableNotice
              state={paymentsState}
              subject="payments"
              message={paymentsQuery.error?.message}
              onRetry={() => paymentsQuery.refetch()}
            />
          )}
          <DataGrid
            columns={paymentColumns}
            data={payments}
            isLoading={paymentsLoading}
            emptyMessage="No payments found"
            unresolvedMessage="Your payments could not be loaded, so this is not a period with no payments received. Try again."
            loadingMessage="Loading payments..."
            exportFilename="payments"
            pageSize={50}
          />
        </>
      )}
      {/* Chat Panel — slide-in from right */}
      {showChat && (
        <div className="fixed inset-y-0 right-0 w-[420px] z-50 shadow-2xl">
          <div className="h-full pt-16 pb-4 pr-4">
            <BillingChat onClose={() => {
              setShowChat(false)
              queryClient.invalidateQueries({ queryKey: ['billingTimeEntries'] })
            }} />
          </div>
        </div>
      )}
    </div>
  )
}

export default Billing
